import React, { useState } from 'react';
import { inpatientAPI, handleApiError } from '../../services/api';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import SelectPaymentModal from './SelectPaymentModal';
import { useTranslation } from 'react-i18next';

const ConvertToInpatientModal = ({
  isOpen,
  onClose,
  visitData,
  isConverting,
  onConvert,
  onSuccess,
  onError
}) => {
  const [downpayment, setDownpayment] = useState(0);
  const [paymentType, setPaymentType] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [specialRequestsNote, setSpecialRequestsNote] = useState("");
  const [lengthOfStayDays, setLengthOfStayDays] = useState(0);
  const [cageNo, setCageNo] = useState("");
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  if (!isOpen) return null;

  const handleConvert = () => {
    if (!visitData) return;

    // If downpayment > 0, show payment type selection modal
    if (downpayment > 0) {
      setShowPaymentModal(true);
    } else {
      // If no downpayment, proceed directly with conversion
      performConversion();
    }
  };

  const performConversion = async (selectedPaymentType = null, paymentNote = "") => {
    if (!visitData) return;

    try {
      await onConvert(visitData.id);

      // Convert outpatient to inpatient with downpayment, payment type, and note
      const paymentData = downpayment > 0 ? {
        payment_type_id: selectedPaymentType || paymentType,
        ...(paymentNote && { payment_note: paymentNote })
      } : {};

      // Include special requests data if available
      const specialRequestsData = {};
      if (specialRequestsNote && specialRequestsNote.trim()) {
        specialRequestsData.special_requests_note = specialRequestsNote.trim();
      }

      // Include cage number and length of stay
      const additionalData = {};
      if (cageNo && cageNo.trim()) {
        additionalData.cage_no = cageNo.trim();
      }
      if (lengthOfStayDays && Number(lengthOfStayDays) > 0) {
        additionalData.length_of_stay_days = Number(lengthOfStayDays);
      }

      const response = await inpatientAPI.convertFromOutpatient(visitData.id, Number(downpayment) || 0, { ...paymentData, ...specialRequestsData, ...additionalData });

      showSuccessToast(t('receptionOutpatients.convert.toasts.success'));

      // Call success callback
      if (onSuccess) {
        onSuccess(visitData);
      }
    } catch (error) {
      console.error('Error converting outpatient to inpatient:', error);
      const message = handleApiError ? handleApiError(error) : null;
      if (message) {
        showErrorToast(message);
      } else {
        showErrorToast(t('receptionOutpatients.convert.toasts.error'));
      }

      // Call error callback
      if (onError) {
        onError(error);
      }
    }
  };

  const handlePaymentSave = ({ method, notes }) => {
    setPaymentType(method);
    setPaymentNotes(notes || "");
    setShowPaymentModal(false);
    performConversion(method, notes || "");
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
  };

  const handleCancel = () => {
    if (!isConverting) {
      setDownpayment(0); // Reset downpayment when canceling
      setPaymentType(""); // Reset payment type
      setPaymentNotes("");
      setCageNo(""); // Reset cage number
      setLengthOfStayDays(0); // Reset length of stay
      onClose();
    }
  };

  return (
    <div className="reception-modal-overlay" dir={i18n.dir()} lang={i18n.language}>
      <div className="reception-modal">
        <div className="reception-modal-header" style={{
          padding: '24px 32px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: isRTL ? 'row-reverse' : 'row',
          background: 'linear-gradient(to right, #ffffff, #f9fafb)'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '22px',
            fontWeight: '700',
            color: '#111827',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '28px' }} aria-hidden="true">🔄</span>
            {t('receptionOutpatients.convert.title')}
          </h2>
          <button
            className="reception-modal-close"
            onClick={handleCancel}
            disabled={isConverting}
            style={{
              background: '#f3f4f6',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6b7280',
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#fee2e2';
              e.target.style.color = '#ef4444';
              e.target.style.transform = 'rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#6b7280';
              e.target.style.transform = 'rotate(0deg)';
            }}
          >
            ✕
          </button>
        </div>
        <div
          className="reception-modal-form"
          style={{
            padding: '24px 32px',
            overflow: 'auto',
            flex: 1,
            direction: isRTL ? 'rtl' : 'ltr',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
          }}
        >
          <div style={{ flex: 1, overflow: 'auto' }}>
            <p style={{
              textAlign: isRTL ? 'right' : 'left',
              color: '#4b5563',
              fontSize: '15px',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              {t('receptionOutpatients.convert.confirmation')}
            </p>

            {visitData && (
              <div style={{
                marginBottom: '24px',
                textAlign: isRTL ? 'right' : 'left',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                padding: '20px',
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '14px', color: '#64748b' }}>
                  <b style={{ color: '#475569' }}>{t('receptionOutpatients.convert.summary.petGuardian')}:</b>
                  <div style={{ color: '#1e293b', fontWeight: '500', marginTop: '4px' }}>{visitData.pet_owner}</div>
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>
                  <b style={{ color: '#475569' }}>{t('receptionOutpatients.convert.summary.petName')}:</b>
                  <div style={{ color: '#1e293b', fontWeight: '500', marginTop: '4px' }}>{visitData.pet?.name || t('receptionOutpatients.convert.summary.fallback.petName')}</div>
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>
                  <b style={{ color: '#475569' }}>{t('receptionOutpatients.convert.summary.vet')}:</b>
                  <div style={{ color: '#1e293b', fontWeight: '500', marginTop: '4px' }}>{visitData.vet || t('receptionOutpatients.convert.summary.fallback.vet')}</div>
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>
                  <b style={{ color: '#475569' }}>{t('receptionOutpatients.convert.summary.date')}:</b>
                  <div style={{ color: '#1e293b', fontWeight: '500', marginTop: '4px' }}>{visitData.date || t('receptionOutpatients.convert.summary.fallback.date')} {visitData.time || ''}</div>
                </div>
              </div>
            )}

            <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#10b981' }}>💵</span> {t('receptionOutpatients.convert.fields.downpayment')}
              </span>
              <input
                type="number"
                className="reception-modal-input"
                value={downpayment ?? ''}
                onChange={(e) => setDownpayment(e.target.value)}
                placeholder={t('receptionOutpatients.convert.fields.downpaymentPlaceholder')}
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  border: '2px solid #f3f4f6',
                  borderRadius: '12px',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#10b981';
                  e.target.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#f3f4f6';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
          </div>

          <div
            className="reception-modal-actions"
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '24px 32px',
              borderTop: '1px solid #f3f4f6',
              flexDirection: isRTL ? 'row-reverse' : 'row',
              background: 'white',
              marginTop: 'auto',
              margin: '0 -32px -24px -32px'
            }}
          >
            <button
              type="button"
              className="reception-action-btn reception-modal-cancel"
              onClick={handleCancel}
              disabled={isConverting}
              style={{
                padding: '12px 28px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: 'white',
                color: '#4b5563',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '120px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {t('common.cancel')}
            </button>
            <div style={{ flex: 1, minWidth: 260, maxWidth: 520 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563', marginBottom: 8 }}>
                {t('receptionInpatients.admit.fields.specialRequests.label', { defaultValue: 'Special Requests' })}
              </div>
              <textarea
                value={specialRequestsNote}
                onChange={(e) => setSpecialRequestsNote(e.target.value)}
                placeholder={t('receptionInpatients.admit.fields.specialRequests.placeholder', { defaultValue: 'Write special requests here...' })}
                style={{
                  width: '100%',
                  minHeight: 44,
                  maxHeight: 90,
                  resize: 'vertical',
                  padding: '12px 14px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: 12,
                  outline: 'none',
                  fontSize: 14,
                  background: 'white',
                }}
              />
            </div>
            <button
              type="button"
              className="reception-action-btn"
              onClick={handleConvert}
              disabled={isConverting}
              style={{
                padding: '12px 32px',
                border: 'none',
                borderRadius: '12px',
                background: isConverting ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isConverting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isConverting ? 'none' : '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                padding: '12px 36px'
              }}
              onMouseEnter={(e) => {
                if (!isConverting) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -2px rgba(59, 130, 246, 0.1)';
                  e.target.style.filter = 'brightness(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isConverting) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)';
                  e.target.style.filter = 'none';
                }
              }}
            >
              {isConverting ? (
                <div style={{
                  width: '18px',
                  height: '18px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
              ) : (
                <span style={{ fontSize: '18px' }}>🔄</span>
              )}
              {isConverting ? t('receptionOutpatients.convert.buttons.converting') : t('receptionOutpatients.convert.buttons.submit')}
            </button>
          </div>
        </div>
      </div>

      <SelectPaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentCancel}
        onSave={handlePaymentSave}
        isLoading={isConverting}
        title={t('receptionOutpatients.convert.payment.title')}
        paymentNotes={paymentNotes}
        paymentType={paymentType}
      />
    </div>
  );
};

export default ConvertToInpatientModal;
